// src/pages/Inventario.js
//
// Página de inventario/catálogo del POS. Cambia de forma según el
// modo activo del AppContext:
//
//   Modo "Todas las sucursales":
//     - Muestra el catálogo de la empresa (independiente de stock).
//     - Sin columna de stock específico.
//     - Los productos se listan agrupados/tal cual — un producto por fila.
//     - Botón "Nuevo producto" activo — crea a nivel empresa.
//
//   Modo "Sucursal X":
//     - Muestra productos del catálogo con stock y precio de esa sucursal.
//     - Requiere resolver el almacén principal de la sucursal primero.
//     - Columna "Stock" muestra stock disponible.
//     - Botón "Nuevo producto" activo — crea a nivel empresa también
//       (todas las sucursales lo ven en su catálogo).

import React, { useEffect, useState } from 'react';
import {
  Box, Card, Typography, Stack, TextField, InputAdornment, Button, Chip,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer,
  IconButton, Alert, CircularProgress, Avatar, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Grid, MenuItem,
} from '@mui/material';
import SearchIcon      from '@mui/icons-material/Search';
import AddIcon         from '@mui/icons-material/Add';
import EditIcon        from '@mui/icons-material/Edit';
import Inventory2Icon  from '@mui/icons-material/Inventory2';
import AllInboxIcon    from '@mui/icons-material/AllInbox';
import StorefrontIcon  from '@mui/icons-material/Storefront';
import RefreshIcon     from '@mui/icons-material/Refresh';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useApp } from '../context/AppContext';
import { productos as apiProductos, almacenes as apiAlmacenes, categoriasCatalogo as apiCats } from '../api/posApi';
import EditorProducto from '../components/EditorProducto';
import DialogMovimientosProducto from '../components/DialogMovimientosProducto';
import SwapVertIcon from '@mui/icons-material/SwapVert';

const formatCOP = (n) =>
  (Number(n) || 0).toLocaleString('es-CO', {
    style: 'currency', currency: 'COP', maximumFractionDigits: 0,
  });

const Inventario = () => {
  const { empresa, sucursales, sucursalActiva, esModoTodas, esModoSucursal, notify } = useApp();

  const [items, setItems]         = useState([]);
  const [buscar, setBuscar]       = useState('');
  const [cargando, setCargando]   = useState(false);
  const [error, setError]         = useState('');
  const [almacenId, setAlmacenId] = useState(null);
  const [nuevoAbierto, setNuevo]  = useState(false);
  const [editorProd, setEditorProd] = useState(null); // producto completo a editar
  const [movimientosProd, setMovimientosProd] = useState(null); // producto para dialog kardex
  const [categorias, setCategorias] = useState([]);
  const [catFiltroId, setCatFiltroId] = useState(null); // padre seleccionada, o null=todas

  const empresaId  = empresa?.empresaId || empresa?.EmpresaId;
  const sucursalId = sucursalActiva?.sucursalId || sucursalActiva?.SucursalId;

  // ─── Cargar catálogo de categorías (mig 038) ───
  useEffect(() => {
    if (!empresaId) { setCategorias([]); return; }
    (async () => {
      try {
        const r = await apiCats.listar(empresaId, true);
        setCategorias(Array.isArray(r) ? r : []);
      } catch { setCategorias([]); }
    })();
  }, [empresaId]);

  // ─── Cargar almacén principal cuando cambia la sucursal ───
  useEffect(() => {
    if (!esModoSucursal || !sucursalId) {
      setAlmacenId(null);
      return;
    }
    (async () => {
      try {
        const alms = await apiAlmacenes.listar(sucursalId);
        const principal = (Array.isArray(alms) ? alms : []).find(a => a.esPrincipal)
                       || (Array.isArray(alms) ? alms[0] : null);
        setAlmacenId(principal ? principal.almacenId : null);
      } catch { setAlmacenId(null); }
    })();
  }, [esModoSucursal, sucursalId]);

  // ─── Cargar productos ───
  const cargar = React.useCallback(async () => {
    if (!empresaId) return;
    setCargando(true); setError('');
    try {
      if (esModoTodas) {
        // Catálogo de la empresa (sin stock específico)
        const r = await apiProductos.listar(empresaId, { soloActivos: true });
        const data = r.data || r.Data || r || [];
        setItems(data.map(mapProductoEmpresa));
      } else if (esModoSucursal && almacenId) {
        // Productos con stock/precio del almacén de la sucursal
        const r = await apiProductos.conStockDeAlmacen(almacenId);
        const raw = r || [];
        // El endpoint devuelve { total, data: [...] }
        const data = raw.data || raw.Data || (Array.isArray(raw) ? raw : []);
        setItems(data.map(mapProductoAlmacen));
      } else {
        setItems([]);
      }
    } catch (e) {
      setError(e.mensajeUsuario || 'No se pudieron cargar los productos.');
      setItems([]);
    } finally {
      setCargando(false);
    }
  }, [empresaId, esModoTodas, esModoSucursal, almacenId]);

  useEffect(() => { cargar(); }, [cargar]);

  const categoriasPadre = categorias.filter(c => !c.categoriaPadreId);
  const catIdsHijasDePadre = (padreId) =>
    new Set([padreId, ...categorias.filter(c => c.categoriaPadreId === padreId).map(c => c.categoriaCatalogoId)]);

  const filtrados = items.filter(it => {
    if (catFiltroId != null) {
      const idsAceptados = catIdsHijasDePadre(catFiltroId);
      if (!idsAceptados.has(it.categoriaCatalogoId)) return false;
    }
    if (!buscar.trim()) return true;
    const q = buscar.toLowerCase();
    return (it.nombre || '').toLowerCase().includes(q)
        || (it.codigo || '').toLowerCase().includes(q)
        || (it.codigoBarras || '').includes(q);
  });

  const alertaSucursalSinAlmacen = esModoSucursal && !almacenId;
  const nombreSucursal = sucursalActiva?.nombre || sucursalActiva?.Nombre;
  const nombreEmpresa  = empresa?.nombreComercial || empresa?.NombreComercial
                     || empresa?.razonSocial     || empresa?.RazonSocial;

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center"
             sx={{ mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Inventory2Icon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4" fontWeight={700}>Inventario</Typography>
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mt: 0.3, flexWrap: 'wrap' }}>
              {esModoTodas ? (
                <Chip size="small" icon={<AllInboxIcon />} label="Catálogo de la empresa"
                      color="primary" variant="outlined" />
              ) : (
                <Chip size="small" icon={<StorefrontIcon />}
                      label={`Sucursal: ${nombreSucursal || '—'}`}
                      color="primary" />
              )}
              {!cargando && items.length > 0 && (
                <Chip
                  size="small"
                  label={
                    buscar.trim() && filtrados.length !== items.length
                      ? `${filtrados.length} de ${items.length} productos`
                      : `${items.length} producto${items.length !== 1 ? 's' : ''}`
                  }
                  variant="outlined"
                />
              )}
            </Stack>
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Actualizar">
            <IconButton onClick={cargar} disabled={cargando}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setNuevo(true)}
                  disabled={!empresaId}>
            Nuevo producto
          </Button>
        </Stack>
      </Stack>

      {alertaSucursalSinAlmacen && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<WarningAmberIcon />}>
          Esta sucursal aún no tiene un almacén configurado. Crea uno para poder cargar y
          controlar stock. (Módulo Almacenes — próximamente).
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>
      )}

      {/* Búsqueda + chips por categoría */}
      <Card sx={{ p: 2, mb: 2 }}>
        <TextField
          fullWidth size="small"
          placeholder="Buscar por nombre, código o código de barras"
          value={buscar}
          onChange={(e) => setBuscar(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        {categoriasPadre.length > 0 && (
          <Stack direction="row" spacing={1} sx={{
            mt: 1.5, overflowX: 'auto', pb: 0.5,
            '&::-webkit-scrollbar': { height: 4 },
          }}>
            <Chip
              label="Todos"
              color={catFiltroId === null ? 'primary' : 'default'}
              variant={catFiltroId === null ? 'filled' : 'outlined'}
              onClick={() => setCatFiltroId(null)}
              size="small"
            />
            {categoriasPadre.map(c => (
              <Chip
                key={c.categoriaCatalogoId}
                label={c.nombre}
                color={catFiltroId === c.categoriaCatalogoId ? 'primary' : 'default'}
                variant={catFiltroId === c.categoriaCatalogoId ? 'filled' : 'outlined'}
                onClick={() => setCatFiltroId(c.categoriaCatalogoId)}
                size="small"
                sx={{ flexShrink: 0 }}
              />
            ))}
          </Stack>
        )}
      </Card>

      {/* Tabla */}
      <Card>
        {cargando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : filtrados.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center' }}>
            <Inventory2Icon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              {items.length === 0
                ? 'No hay productos en el catálogo.'
                : 'Ningún producto coincide con la búsqueda.'}
            </Typography>
            {items.length === 0 && (
              <Button variant="contained" startIcon={<AddIcon />} sx={{ mt: 2 }}
                      onClick={() => setNuevo(true)} disabled={!empresaId}>
                Crear el primer producto
              </Button>
            )}
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={60} />
                  <TableCell>Producto</TableCell>
                  <TableCell>Código</TableCell>
                  <TableCell align="right">Precio</TableCell>
                  {esModoSucursal && <TableCell align="right">Stock</TableCell>}
                  <TableCell align="center">Estado</TableCell>
                  <TableCell align="right">Acciones</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtrados.map((p) => (
                  <TableRow key={p.productoId} hover>
                    <TableCell>
                      <Avatar src={p.imagenUrl} variant="rounded"
                              sx={{ width: 44, height: 44, bgcolor: 'grey.100' }}>
                        <Inventory2Icon fontSize="small" color="disabled" />
                      </Avatar>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={500} noWrap>
                        {p.nombre}
                      </Typography>
                      {p.categoriaNombre && (
                        <Typography variant="caption" color="text.secondary">
                          {p.categoriaNombre}{p.marcaNombre && ` · ${p.marcaNombre}`}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                        {p.codigo}
                      </Typography>
                      {p.codigoBarras && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {p.codigoBarras}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={600}>
                        {formatCOP(p.precio)}
                      </Typography>
                    </TableCell>
                    {esModoSucursal && (
                      <TableCell align="right">
                        {p.controlaInventario ? (
                          <Chip
                            size="small" label={p.stockDisponible ?? 0}
                            color={
                              (p.stockDisponible ?? 0) === 0 ? 'error' :
                              (p.stockDisponible ?? 0) < 10  ? 'warning' :
                                                               'success'
                            }
                            variant={((p.stockDisponible ?? 0) === 0) ? 'filled' : 'outlined'}
                          />
                        ) : (
                          <Chip size="small" label="—" variant="outlined" />
                        )}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      {p.activo === false ? (
                        <Chip size="small" label="Inactivo" />
                      ) : p.visibleEnPOS === false ? (
                        <Chip size="small" label="Oculto" color="default" variant="outlined" />
                      ) : (
                        <Chip size="small" label="Activo" color="success" variant="outlined" />
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <IconButton size="small" title="Movimientos y kardex"
                                  onClick={() => setMovimientosProd(p)}>
                        <SwapVertIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" title="Editar producto"
                                  onClick={async () => {
                                    try {
                                      const detalle = await apiProductos.obtener(p.productoId);
                                      setEditorProd(detalle);
                                    } catch (e) {
                                      setError(e.mensajeUsuario || 'No se pudo cargar el producto.');
                                    }
                                  }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <Box sx={{ p: 2, textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary">
            {filtrados.length} producto{filtrados.length !== 1 && 's'}
            {esModoTodas && ' en el catálogo de la empresa'}
            {esModoSucursal && ` en ${nombreSucursal}`}
          </Typography>
        </Box>
      </Card>

      {nuevoAbierto && (
        <EditorProducto
          modo="crear"
          empresaId={empresaId}
          categorias={categorias}
          onClose={() => setNuevo(false)}
          onSaved={async () => {
            setNuevo(false);
            await cargar();
            notify('Producto creado.', 'success');
          }}
          onError={(m) => notify(m, 'error')}
        />
      )}
      {editorProd && (
        <EditorProducto
          modo="editar"
          producto={editorProd}
          empresaId={empresaId}
          categorias={categorias}
          onClose={() => setEditorProd(null)}
          onSaved={async () => {
            setEditorProd(null);
            await cargar();
            notify('Producto actualizado.', 'success');
          }}
          onError={(m) => notify(m, 'error')}
        />
      )}
      {movimientosProd && (
        <DialogMovimientosProducto
          producto={movimientosProd}
          empresaId={empresaId}
          sucursales={sucursales}
          notify={notify}
          onClose={() => setMovimientosProd(null)}
          onDidChange={cargar}
        />
      )}
    </Box>
  );
};

// ═════════════════════════════════════════════════════════════
// Adaptadores
// ═════════════════════════════════════════════════════════════

function mapProductoEmpresa(p) {
  return {
    productoId:   p.productoId ?? p.ProductoId,
    codigo:       p.codigo ?? p.Codigo,
    codigoBarras: p.codigoBarras ?? p.CodigoBarras,
    nombre:       p.nombre ?? p.Nombre,
    imagenUrl:    p.imagenUrl ?? p.ImagenUrl,
    precio:       p.precioVenta ?? p.PrecioVenta ?? 0,
    categoriaNombre: p.categoriaCatalogoNombre ?? p.CategoriaCatalogoNombre
                   ?? p.categoria?.nombre ?? p.Categoria?.Nombre,
    categoriaCatalogoId: p.categoriaCatalogoId ?? p.CategoriaCatalogoId ?? null,
    marcaNombre:  p.marca?.nombre ?? p.Marca?.Nombre,
    activo:       p.activo ?? p.Activo ?? true,
    visibleEnPOS: p.visibleEnPOS ?? p.VisibleEnPOS,
    controlaInventario: p.controlaInventario ?? p.ControlaInventario ?? false,
    stockDisponible: null,
  };
}

function mapProductoAlmacen(p) {
  return {
    productoId:   p.productoId,
    codigo:       p.codigo,
    codigoBarras: p.codigoBarras,
    nombre:       p.nombre,
    imagenUrl:    p.imagenUrl,
    precio:       p.precio ?? p.precioBase ?? 0,
    categoriaNombre: p.categoriaCatalogoNombre ?? null,
    categoriaCatalogoId: p.categoriaCatalogoId ?? null,
    marcaNombre:  null,
    activo:       true,
    visibleEnPOS: p.visibleEnPOS,
    controlaInventario: p.controlaInventario,
    stockDisponible: p.stockDisponible,
  };
}

// ═════════════════════════════════════════════════════════════
// Dialog crear producto (versión mínima)
// ═════════════════════════════════════════════════════════════


export default Inventario;
